import * as THREE from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { ARENA, OBSTACLES, BUILDINGS, HILL, terrainHeight, type Obstacle, type BuildingMeta } from "@tank/shared";
import { buildGrassField } from "./GrassField";
import { buildVegetation } from "./Vegetation";

const PROPS: ReadonlyArray<{ x: number; z: number; kind: "crate" | "barrel" }> = [
  { x: -8, z: -6, kind: "crate" },
  { x: 9, z: 7, kind: "barrel" },
  { x: 6, z: -9, kind: "crate" },
  { x: -10, z: 8, kind: "barrel" },
  { x: 14, z: -2, kind: "crate" },
  { x: -14, z: 2, kind: "barrel" },
  { x: 0, z: 18, kind: "crate" },
  { x: 0, z: -18, kind: "barrel" },
];

/** Small deterministic PRNG seeded from an obstacle's own position, so decorative
 * jitter (rock/crate placement) is stable across reloads without a stored seed. */
function seededRandom(seed: number): () => number {
  let s = Math.abs(Math.round(seed)) + 1;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return (s % 1000) / 1000;
  };
}

/** Reuses an already-configured color texture (repeat/wrap already set) as a
 * bump map — cheap, real perceived surface relief (mortar joints, wood grain,
 * weave, panel seams…) for free, no extra texture authoring. Cloned rather
 * than shared because bump sampling must read raw/linear values, not the
 * gamma-decoded ones the same texture gets as a `map` — so the clone's
 * colorSpace is stripped independently of the source. */
function bumpOf(mapTex: THREE.Texture): THREE.Texture {
  const b = mapTex.clone();
  b.needsUpdate = true;
  b.colorSpace = THREE.NoColorSpace;
  return b;
}

// ── Modular obstacle visuals ────────────────────────────────────────────
// Collision is ALWAYS the plain w/d/h AABB (see @tank/shared Obstacle + the
// server's ArenaBuilder) — these builders only vary the MESH inside that same
// footprint, so gameplay never depends on which kind renders. Shapes are
// chosen to read as real, recognizable cover instead of abstract blocks.
type ObstacleBuilder = (o: Obstacle) => THREE.Object3D;

/** Tileable canvas texture: tan fabric weave + mud/wear blotches, for sandbags. */
function makeFabricTexture(): THREE.CanvasTexture {
  const s = 96;
  const c = document.createElement("canvas");
  c.width = s; c.height = s;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#b9a06a";
  ctx.fillRect(0, 0, s, s);
  ctx.strokeStyle = "rgba(90,74,40,0.35)";
  ctx.lineWidth = 1;
  for (let i = -s; i < s * 2; i += 5) {
    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i - s, s); ctx.stroke();
  }
  for (let i = 0; i < 26; i++) {
    ctx.fillStyle = `rgba(70,58,30,${0.08 + Math.random() * 0.14})`;
    const x = Math.random() * s, y = Math.random() * s, r = 4 + Math.random() * 10;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Tileable canvas texture: weathered wood planks, for crates. */
function makeWoodTexture(): THREE.CanvasTexture {
  const s = 96;
  const c = document.createElement("canvas");
  c.width = s; c.height = s;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#8a5a33";
  ctx.fillRect(0, 0, s, s);
  const plankH = s / 4;
  for (let row = 0; row < 4; row++) {
    const y = row * plankH;
    ctx.fillStyle = row % 2 === 0 ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";
    ctx.fillRect(0, y, s, plankH);
    ctx.strokeStyle = "rgba(40,22,10,0.55)";
    ctx.beginPath(); ctx.moveTo(0, y + plankH - 1); ctx.lineTo(s, y + plankH - 1); ctx.stroke();
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(50,28,12,${0.08 + Math.random() * 0.1})`;
      const gy = y + Math.random() * plankH;
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(s, gy + (Math.random() * 4 - 2)); ctx.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Tileable canvas texture: weathered concrete panels, for barriers/roofs/fallback. */
function makeConcreteTexture(): THREE.CanvasTexture {
  const s = 96;
  const c = document.createElement("canvas");
  c.width = s; c.height = s;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#8a8f94";
  ctx.fillRect(0, 0, s, s);
  ctx.strokeStyle = "rgba(60,64,68,0.5)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(1, 1, s - 2, s - 2);
  ctx.beginPath(); ctx.moveTo(s / 2, 0); ctx.lineTo(s / 2, s); ctx.stroke();
  for (let i = 0; i < 40; i++) {
    ctx.fillStyle = `rgba(${Math.random() > 0.5 ? "40,42,44" : "200,202,205"},${0.04 + Math.random() * 0.08})`;
    const x = Math.random() * s, y = Math.random() * s, r = 1 + Math.random() * 3;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const fabricTex = makeFabricTexture();
const sandbagMat = new THREE.MeshStandardMaterial({ map: fabricTex, bumpMap: bumpOf(fabricTex), bumpScale: 0.04, roughness: 0.95 });
// A few subtly-different rock shades (picked per-rock, see buildRockCluster)
// so a cluster reads as natural stone rather than identical clones.
const rockMatVariants = [0x6b6459, 0x716a5d, 0x625c53, 0x76705f].map(
  (color) => new THREE.MeshStandardMaterial({ color, roughness: 0.95, flatShading: true }),
);
const concreteTex = makeConcreteTexture();
const concreteMat = new THREE.MeshStandardMaterial({ map: concreteTex, bumpMap: bumpOf(concreteTex), bumpScale: 0.035, roughness: 0.85 });
const hazardStripeMat = new THREE.MeshStandardMaterial({ color: 0xffc04d, roughness: 0.5, emissive: 0x3a2a00, emissiveIntensity: 0.35 });
const woodTex = makeWoodTexture();
const crateMat2 = new THREE.MeshStandardMaterial({ map: woodTex, bumpMap: bumpOf(woodTex), bumpScale: 0.03, roughness: 0.85 });
const crateEdgeMat = new THREE.LineBasicMaterial({ color: 0x3d2612 });

/** Stacked, staggered sandbags — reads instantly as a hard-cover bunker wall. */
function buildSandbags(o: Obstacle): THREE.Object3D {
  const g = new THREE.Group();
  const bagLen = 0.9;
  const bagH = 0.42;
  const rows = Math.min(4, Math.max(2, Math.round(o.h / bagH)));
  const cols = Math.min(6, Math.max(2, Math.round(o.w / (bagLen * 0.85))));
  const depthCols = Math.min(3, Math.max(1, Math.round(o.d / (bagLen * 0.85))));
  for (let row = 0; row < rows; row++) {
    const y = bagH * 0.55 + row * bagH * 0.9;
    const stagger = (row % 2) * (o.w / cols) * 0.5;
    for (let c = 0; c < cols; c++) {
      const x = -o.w / 2 + (o.w / cols) * (c + 0.5) + stagger;
      if (x > o.w / 2 - bagLen * 0.2) continue;
      for (let dc = 0; dc < depthCols; dc++) {
        const z = -o.d / 2 + (o.d / depthCols) * (dc + 0.5);
        const bag = new THREE.Mesh(new THREE.CapsuleGeometry(bagH * 0.42, bagLen * 0.5, 3, 7), sandbagMat);
        bag.rotation.z = Math.PI / 2;
        bag.position.set(x, y, z);
        g.add(bag);
      }
    }
  }
  g.position.set(o.x, 0, o.z);
  return g;
}

/** A cluster of irregular low-poly boulders — reads as natural rocky cover. */
function buildRockCluster(o: Obstacle): THREE.Object3D {
  const g = new THREE.Group();
  const rand = seededRandom(o.x * 13 + o.z * 7);
  const count = 5;
  for (let i = 0; i < count; i++) {
    const s = (0.32 + rand() * 0.38) * Math.min(o.w, o.d, o.h * 1.4);
    const mat = rockMatVariants[Math.floor(rand() * rockMatVariants.length) % rockMatVariants.length];
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(s, 0), mat);
    rock.position.set(
      (rand() * 2 - 1) * Math.max(0, o.w / 2 - s * 0.6),
      s * 0.55,
      (rand() * 2 - 1) * Math.max(0, o.d / 2 - s * 0.6),
    );
    rock.rotation.set(rand() * Math.PI, rand() * Math.PI, rand() * Math.PI);
    rock.scale.y *= 0.7 + rand() * 0.35;
    g.add(rock);
  }
  g.position.set(o.x, terrainHeight(o.x, o.z), o.z);
  return g;
}

/** Jersey-barrier profile (flared base, narrow flat top) with a hazard stripe. */
function buildConcreteBarrier(o: Obstacle): THREE.Object3D {
  const halfD = o.d / 2;
  const topD = halfD * 0.55;
  const shape = new THREE.Shape();
  shape.moveTo(-halfD, 0);
  shape.lineTo(halfD, 0);
  shape.lineTo(topD, o.h * 0.55);
  shape.lineTo(topD, o.h);
  shape.lineTo(-topD, o.h);
  shape.lineTo(-topD, o.h * 0.55);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: o.w, steps: 1, bevelEnabled: false });
  geo.translate(0, 0, -o.w / 2); // center the extrusion (length) axis
  geo.rotateY(Math.PI / 2); // length (o.w) → world X, profile (o.d) → world Z
  const body = new THREE.Mesh(geo, concreteMat);
  const g = new THREE.Group();
  g.add(body);
  const stripe = new THREE.Mesh(new THREE.BoxGeometry(o.w * 0.96, o.h * 0.16, o.d * 0.55), hazardStripeMat);
  stripe.position.y = o.h * 0.6;
  g.add(stripe);
  g.position.set(o.x, terrainHeight(o.x, o.z), o.z);
  return g;
}

/** A staggered stack of wooden crates with visible dark edge lines. */
function buildCrateStack(o: Obstacle): THREE.Object3D {
  const g = new THREE.Group();
  const rand = seededRandom(o.x * 17 + o.z * 11 + 3);
  const crateSize = Math.min(o.w, o.d, o.h) * 0.85 || 1;
  const cols = Math.min(4, Math.max(1, Math.round(o.w / crateSize)));
  const depthCols = Math.min(3, Math.max(1, Math.round(o.d / crateSize)));
  const rows = Math.min(3, Math.max(1, Math.round(o.h / crateSize)));
  for (let row = 0; row < rows; row++) {
    for (let c = 0; c < cols; c++) {
      for (let dc = 0; dc < depthCols; dc++) {
        const size = crateSize * (0.85 + rand() * 0.25);
        const box = new THREE.Mesh(new THREE.BoxGeometry(size, size, size), crateMat2);
        box.position.set(
          -o.w / 2 + (o.w / cols) * (c + 0.5) + (rand() - 0.5) * 0.15,
          size / 2 + row * crateSize * 0.95,
          -o.d / 2 + (o.d / depthCols) * (dc + 0.5) + (rand() - 0.5) * 0.15,
        );
        box.rotation.y = (rand() - 0.5) * 0.3;
        box.add(new THREE.LineSegments(new THREE.EdgesGeometry(box.geometry), crateEdgeMat));
        g.add(box);
      }
    }
  }
  g.position.set(o.x, terrainHeight(o.x, o.z), o.z);
  return g;
}

/** Fallback for any obstacle without a recognized `kind` — plain block. */
function buildDefault(o: Obstacle): THREE.Object3D {
  const m = new THREE.Mesh(new THREE.BoxGeometry(o.w, o.h, o.d), concreteMat);
  m.position.set(o.x, terrainHeight(o.x, o.z) + o.h / 2, o.z);
  return m;
}

/** Tileable brick texture (canvas-generated, no external assets) for building walls. */
function makeBrickTexture(): THREE.CanvasTexture {
  const w = 128, h = 128;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#6b3b30"; // mortar base
  ctx.fillRect(0, 0, w, h);
  const brickW = 32, brickH = 14, mortar = 2;
  ctx.fillStyle = "#9a5b46";
  for (let row = 0, y = 0; y < h; row++, y += brickH + mortar) {
    const offset = (row % 2) * (brickW / 2);
    for (let x = -brickW; x < w + brickW; x += brickW + mortar) {
      const bx = x + offset;
      const shade = 0.85 + ((bx * 7 + y * 13) % 11) / 55; // subtle per-brick variation
      ctx.fillStyle = `rgba(154,91,70,${shade.toFixed(2)})`;
      ctx.fillRect(bx, y, brickW, brickH);
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const brickTex = makeBrickTexture();
// Cache one texture+material per distinct (repeatX, repeatY, tint) triple
// instead of cloning a brand new texture/material for every wall segment —
// buildings only use a handful of distinct wall sizes, so this collapses 15
// unique materials down to a handful, cutting redundant draw-call/material
// state and GPU texture memory for no visual difference.
const wallMatCache = new Map<string, THREE.Material>();
function wallMaterialFor(repeatX: number, repeatY: number, tint = 0xffffff): THREE.Material {
  const key = `${repeatX.toFixed(2)}_${repeatY.toFixed(2)}_${tint.toString(16)}`;
  let mat = wallMatCache.get(key);
  if (!mat) {
    const tex = brickTex.clone();
    tex.needsUpdate = true;
    tex.repeat.set(repeatX, repeatY);
    mat = new THREE.MeshStandardMaterial({ map: tex, bumpMap: bumpOf(tex), bumpScale: 0.03, roughness: 0.92, color: tint });
    wallMatCache.set(key, mat);
  }
  return mat;
}

// Per-building tint (index matches @tank/shared BUILDINGS) so the two
// otherwise-identical 14x12 buildings read as distinct structures instead of
// mirror-image clones: Building A stays a clean brick red, Building B gets a
// darker, sootier/weathered tint (Tower C stays default).
const BUILDING_TINTS: readonly number[] = [0xffffff, 0x8f8074, 0xffffff];

function findBuildingIndex(o: Obstacle): number {
  for (let i = 0; i < BUILDINGS.length; i++) {
    const b = BUILDINGS[i];
    if (Math.abs(o.x - b.x) <= b.w / 2 + 1 && Math.abs(o.z - b.z) <= b.d / 2 + 1) return i;
  }
  return -1;
}

/**
 * A single wall segment of a hollow, walkable building (see the `wall`-kind
 * entries in @tank/shared OBSTACLES): a plain textured slab sized exactly to
 * its collider AABB. Several of these placed side-by-side (with gaps for
 * doors) form a real building footprint instead of one big bounding box.
 */
function buildWallSegment(o: Obstacle): THREE.Object3D {
  const bi = findBuildingIndex(o);
  const tint = bi >= 0 ? BUILDING_TINTS[bi] ?? 0xffffff : 0xffffff;
  const mat = wallMaterialFor(Math.max(1, o.w / 3), Math.max(1, o.h / 1.4), tint);
  const m = new THREE.Mesh(new THREE.BoxGeometry(o.w, o.h, o.d), mat);
  m.position.set(o.x, terrainHeight(o.x, o.z) + o.h / 2, o.z);
  return m;
}

const OBSTACLE_BUILDERS: Record<string, ObstacleBuilder> = {
  sandbags: buildSandbags,
  rocks: buildRockCluster,
  concrete: buildConcreteBarrier,
  crates: buildCrateStack,
  wall: buildWallSegment,
};

/** Soft radial dark gradient (canvas-generated) — a cheap "fake AO" contact
 * shadow decal, laid flat on the ground under obstacles/buildings so they
 * read as grounded/weighty instead of floating on the grass. */
function makeContactShadowTexture(): THREE.CanvasTexture {
  const s = 128;
  const c = document.createElement("canvas");
  c.width = s;
  c.height = s;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  g.addColorStop(0, "rgba(0,0,0,0.4)");
  g.addColorStop(0.7, "rgba(0,0,0,0.18)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

const contactShadowTex = makeContactShadowTexture();
const contactShadowMat = new THREE.MeshBasicMaterial({
  map: contactShadowTex, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1,
});

/** Lays a soft contact-shadow decal flat on the (possibly sloped) terrain under a footprint. */
function addContactShadow(scene: THREE.Scene, x: number, z: number, rx: number, rz: number): void {
  const geo = new THREE.PlaneGeometry(rx * 2, rz * 2);
  const m = new THREE.Mesh(geo, contactShadowMat);
  m.rotation.x = -Math.PI / 2;
  m.position.set(x, terrainHeight(x, z) + 0.03, z);
  scene.add(m);
}

/** Diagonal criss-cross chain-link pattern, transparent background — a fence
 * topper for the arena perimeter so it reads as a militarized compound
 * boundary instead of a flat colored slab. */
function makeChainLinkTexture(): THREE.CanvasTexture {
  const s = 64;
  const c = document.createElement("canvas");
  c.width = s;
  c.height = s;
  const ctx = c.getContext("2d")!;
  ctx.strokeStyle = "rgba(200,205,210,0.8)";
  ctx.lineWidth = 1.3;
  for (let x = -s; x < s * 2; x += 9) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + s, s); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, s); ctx.lineTo(x + s, 0); ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

const fenceTex = makeChainLinkTexture();
const fenceMat = new THREE.MeshStandardMaterial({
  map: fenceTex, bumpMap: bumpOf(fenceTex), bumpScale: 0.015,
  transparent: true, side: THREE.DoubleSide, roughness: 0.5, metalness: 0.5, alphaTest: 0.1,
});
const postMat = new THREE.MeshStandardMaterial({ color: 0x4a4f54, roughness: 0.7, metalness: 0.4 });
const trimStripMat = new THREE.MeshStandardMaterial({ map: concreteTex, bumpMap: bumpOf(concreteTex), bumpScale: 0.035, roughness: 0.85, color: 0x565b5f });
const doorFrameMat = new THREE.MeshStandardMaterial({ color: 0x2b211b, roughness: 0.8 });
const windowFrameMat = new THREE.MeshStandardMaterial({ color: 0x3a342c, roughness: 0.7 });
const windowGlassMat = new THREE.MeshStandardMaterial({ color: 0x1a2530, roughness: 0.25, metalness: 0.3, emissive: 0x0d1620, emissiveIntensity: 0.25 });

/**
 * A tall, heavily-fortified arena perimeter run: textured concrete barrier +
 * evenly-spaced support posts + a chain-link fence topper. Built as one long
 * body plus posts/fence rather than a flat colored box (the old look) so the
 * map border reads as a real militarized boundary at any distance.
 */
function buildPerimeterWall(scene: THREE.Scene, x: number, z: number, w: number, d: number, wh: number): void {
  const isXRun = w >= d;
  const length = Math.max(w, d);

  const tex = concreteTex.clone();
  tex.needsUpdate = true;
  const reps = Math.max(1, Math.round(length / 4));
  if (isXRun) tex.repeat.set(reps, 1); else tex.repeat.set(1, reps);
  const bodyMat = new THREE.MeshStandardMaterial({ map: tex, bumpMap: bumpOf(tex), bumpScale: 0.035, roughness: 0.88, color: 0x6b7075 });

  const body = new THREE.Mesh(new THREE.BoxGeometry(w, wh, d), bodyMat);
  body.position.set(x, wh / 2, z);
  body.castShadow = true;
  body.receiveShadow = true;
  scene.add(body);

  // Chain-link fence topper, doubling the effective height without reading
  // as a solid wall (you can see — and shoot — through it).
  const fenceH = 1.8;
  const fence = new THREE.Mesh(new THREE.PlaneGeometry(isXRun ? w : d, fenceH), fenceMat);
  fence.position.set(x, wh + fenceH / 2, z);
  if (!isXRun) fence.rotation.y = Math.PI / 2;
  scene.add(fence);

  // Support posts every ~9m, proud of the wall and taller than the fence so
  // they read as real structural members holding it up.
  const postH = wh + fenceH + 0.3;
  const spacing = 9;
  const count = Math.max(2, Math.round(length / spacing));
  for (let i = 0; i <= count; i++) {
    const t2 = -length / 2 + (length * i) / count;
    const px = isXRun ? x + t2 : x;
    const pz = isXRun ? z : z + t2;
    const post = new THREE.Mesh(new THREE.BoxGeometry(isXRun ? 0.5 : 0.9, postH, isXRun ? 0.9 : 0.5), postMat);
    post.position.set(px, postH / 2, pz);
    post.castShadow = true;
    scene.add(post);
  }
}

/**
 * Visual-only detailing for one hollow building (door frame, foundation
 * plinth, roof parapet, corner quoins, and a couple of windows on the
 * door-free side walls) — layered on top of the plain brick `wall` OBSTACLES
 * segments to read as a real structure instead of a box with a hole in it.
 */
function addBuildingDetails(scene: THREE.Scene, b: BuildingMeta): void {
  const y0 = terrainHeight(b.x, b.z);

  // Foundation plinth: a wider, darker slab peeking out at the base.
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.8, 0.4, b.d + 0.8), trimStripMat);
  plinth.position.set(b.x, y0 + 0.2, b.z);
  plinth.castShadow = true;
  plinth.receiveShadow = true;
  scene.add(plinth);

  // Roof parapet: a thin raised lip around the roof's edge (roof itself is
  // still added by the caller, matching its existing overhang size).
  const roofY = y0 + b.wallH + 0.35;
  const lipH = 0.4;
  const lip = (lx: number, lz: number, lw: number, ld: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(lw, lipH, ld), trimStripMat);
    m.position.set(lx, roofY + lipH / 2, lz);
    m.castShadow = true;
    scene.add(m);
  };
  const rw = b.w + 0.6, rd = b.d + 0.6, rt = 0.35;
  lip(b.x, b.z - rd / 2 + rt / 2, rw, rt);
  lip(b.x, b.z + rd / 2 - rt / 2, rw, rt);
  lip(b.x - rw / 2 + rt / 2, b.z, rt, rd);
  lip(b.x + rw / 2 - rt / 2, b.z, rt, rd);

  // Corner quoins: vertical accent posts breaking up the flat brick faces.
  const hw = b.w / 2, hd = b.d / 2;
  for (const [cx, cz] of [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]] as const) {
    const q = new THREE.Mesh(new THREE.BoxGeometry(0.5, b.wallH, 0.5), trimStripMat);
    q.position.set(b.x + cx, y0 + b.wallH / 2, b.z + cz);
    q.castShadow = true;
    q.receiveShadow = true;
    scene.add(q);
  }

  // Door frame: a lintel over the gap + two posts at its edges.
  const half = b.doorWidth / 2;
  const vertical = b.doorSide === "n" || b.doorSide === "s";
  const wallZ = b.doorSide === "n" ? b.z + hd : b.doorSide === "s" ? b.z - hd : b.z;
  const wallX = b.doorSide === "e" ? b.x + hw : b.doorSide === "w" ? b.x - hw : b.x;
  const lintel = new THREE.Mesh(
    new THREE.BoxGeometry(vertical ? b.doorWidth + 0.6 : 0.5, 0.5, vertical ? 0.5 : b.doorWidth + 0.6),
    doorFrameMat,
  );
  lintel.position.set(vertical ? b.doorCenter : wallX, y0 + b.wallH - 0.55, vertical ? wallZ : b.doorCenter);
  lintel.castShadow = true;
  scene.add(lintel);
  for (const side of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(vertical ? 0.35 : 0.5, b.wallH - 0.5, vertical ? 0.5 : 0.35), doorFrameMat);
    const px = vertical ? b.doorCenter + side * (half + 0.15) : wallX;
    const pz = vertical ? wallZ : b.doorCenter + side * (half + 0.15);
    post.position.set(px, y0 + (b.wallH - 0.5) / 2, pz);
    post.castShadow = true;
    scene.add(post);
  }

  // Windows: a couple on each side wall that ISN'T the door wall, so every
  // building shows some window detail regardless of which side its door is on.
  const winY = y0 + b.wallH * 0.6;
  const addWindow = (wx: number, wz: number, faceX: boolean) => {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(faceX ? 0.08 : 1.1, 1.1, faceX ? 1.1 : 0.08), windowFrameMat);
    frame.position.set(wx, winY, wz);
    scene.add(frame);
    const glass = new THREE.Mesh(new THREE.BoxGeometry(faceX ? 0.06 : 0.85, 0.85, faceX ? 0.85 : 0.06), windowGlassMat);
    glass.position.set(wx + (faceX ? 0.02 : 0), winY, wz + (faceX ? 0 : 0.02));
    scene.add(glass);
  };
  if (b.doorSide !== "e" && b.doorSide !== "w") {
    // door is on n/s, so put windows on the e/w faces
    addWindow(b.x + hw + 0.04, b.z - hd * 0.4, true);
    addWindow(b.x + hw + 0.04, b.z + hd * 0.4, true);
    addWindow(b.x - hw - 0.04, b.z - hd * 0.4, true);
    addWindow(b.x - hw - 0.04, b.z + hd * 0.4, true);
  } else {
    addWindow(b.x - hw * 0.4, b.z + hd + 0.04, false);
    addWindow(b.x + hw * 0.4, b.z + hd + 0.04, false);
    addWindow(b.x - hw * 0.4, b.z - hd - 0.04, false);
    addWindow(b.x + hw * 0.4, b.z - hd - 0.04, false);
  }
}

/** Tileable, hand-painted-looking grass texture (canvas-generated, no external assets). */
function makeGrassTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#3f7a34";
  ctx.fillRect(0, 0, size, size);
  const shades = ["#376c2e", "#488a3b", "#579a45", "#2f5e28"];
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = shades[i % shades.length];
    ctx.globalAlpha = 0.3;
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 2 + Math.random() * 6;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Displaces a ground PlaneGeometry's vertices by `terrainHeight` (rolling
 * hills, flattened around spawns/power-ups/obstacles/walls — see
 * @tank/shared terrain.ts) and recomputes normals for correct lighting on
 * slopes. Vertex Z (the plane's pre-rotation "up" axis) becomes world Y once
 * the mesh is rotated -90° about X, so world XZ = (local X, -local Y).
 */
function displaceTerrain(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const worldX = pos.getX(i);
    const worldZ = -pos.getY(i);
    pos.setZ(i, terrainHeight(worldX, worldZ));
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

/** Handle returned for the King of the Hill capture zone marker — a flat,
 * unlit ring + faint fill disc (so it stays clearly visible regardless of
 * the scene's dusk-toned lighting) sitting on the terrain at the shared HILL
 * coordinates. Built into every arena (the same static map is reused for all
 * modes) but hidden by default; main.ts shows it and recolors it (by holder,
 * or neutral gray when unclaimed/contested) only once it knows the joined
 * room is actually "koth". */
export interface HillMarker {
  group: THREE.Group;
  setColor(hex: number): void;
}

function buildHillMarker(scene: THREE.Scene): HillMarker {
  const g = new THREE.Group();
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(HILL.RADIUS, 48),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.12, depthWrite: false, side: THREE.DoubleSide }),
  );
  disc.rotation.x = -Math.PI / 2;
  g.add(disc);
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(HILL.RADIUS - 0.4, HILL.RADIUS, 48),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.03;
  g.add(ring);
  g.position.set(HILL.X, terrainHeight(HILL.X, HILL.Z) + 0.05, HILL.Z);
  g.renderOrder = 1;
  g.visible = false;
  scene.add(g);
  return {
    group: g,
    setColor(hex: number) {
      (disc.material as THREE.MeshBasicMaterial).color.setHex(hex);
      (ring.material as THREE.MeshBasicMaterial).color.setHex(hex);
    },
  };
}

/** Builds the visual arena (grass floor, walls, obstacles, props, grass blades) — all shadowed. */
export function buildArena(scene: THREE.Scene): HillMarker {
  const size = ARENA.HALF * 2;
  // One segment per ~meter, capped so a much bigger arena doesn't blow up the
  // vertex count — this is a static mesh (built once), so the cap only guards
  // against an unreasonably large ARENA.HALF, not per-frame cost.
  const segments = Math.min(160, Math.max(40, Math.round(size)));

  const grassTex = makeGrassTexture();
  grassTex.repeat.set(size / 6, size / 6);
  const floorGeo = new THREE.PlaneGeometry(size, size, segments, segments);
  displaceTerrain(floorGeo);
  const floor = new THREE.Mesh(
    floorGeo,
    // bumpMap adds fine blade-level grain on top of the terrain's own
    // (geometric, macro-scale) rolling-hill displacement — the two operate at
    // completely different scales so they layer instead of fighting.
    new THREE.MeshStandardMaterial({ map: grassTex, bumpMap: bumpOf(grassTex), bumpScale: 0.06, roughness: 0.95 }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  scene.add(floor);

  const H = ARENA.HALF;
  const t = ARENA.WALL_THICKNESS;
  const wh = ARENA.WALL_HEIGHT;
  const span = size + t;
  buildPerimeterWall(scene, 0, -H, span, t, wh);
  buildPerimeterWall(scene, 0, H, span, t, wh);
  buildPerimeterWall(scene, -H, 0, t, span, wh);
  buildPerimeterWall(scene, H, 0, t, span, wh);

  for (const o of OBSTACLES) {
    const build = OBSTACLE_BUILDERS[o.kind ?? ""] ?? buildDefault;
    const obj = build(o);
    obj.traverse((n) => {
      if (n instanceof THREE.Mesh) {
        n.castShadow = true;
        n.receiveShadow = true;
      }
    });
    scene.add(obj);
    // Contact shadow: skip individual `wall` segments (each building gets one
    // shadow for its whole footprint below, instead of overlapping per-segment
    // patches at right angles).
    if (o.kind !== "wall") addContactShadow(scene, o.x, o.z, (o.w / 2) * 1.25, (o.d / 2) * 1.25);
  }

  // Building roofs — visual-only slabs atop each hollow building's walls (see
  // @tank/shared BUILDINGS metadata; the walls themselves are plain `wall`-kind
  // OBSTACLES entries, rendered above). Slightly overhangs the walls and reuses
  // the concrete texture at a rougher, darker tint to read as a distinct material.
  // Semi-transparent so a chasing camera (or a curious glance) can read some
  // of the interior/silhouette through the roof instead of a fully opaque cap.
  const roofMat = new THREE.MeshStandardMaterial({
    map: concreteTex, bumpMap: bumpOf(concreteTex), bumpScale: 0.03, roughness: 0.9, color: 0x6b6f73, transparent: true, opacity: 0.55,
  });
  for (const b of BUILDINGS) {
    const roof = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.35, b.d + 0.6), roofMat);
    roof.position.set(b.x, terrainHeight(b.x, b.z) + b.wallH + 0.17, b.z);
    roof.castShadow = true;
    roof.receiveShadow = true;
    scene.add(roof);
    addContactShadow(scene, b.x, b.z, (b.w / 2) * 1.2, (b.d / 2) * 1.2);
    addBuildingDetails(scene, b);
  }

  // Decorative props.
  const crateMat = new THREE.MeshStandardMaterial({ color: 0x8a5a33, roughness: 0.85 });
  const barrelMat = new THREE.MeshStandardMaterial({ color: 0xb5482e, roughness: 0.6, metalness: 0.3 });
  for (const p of PROPS) {
    const m = p.kind === "crate"
      ? new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.4, 1.4), crateMat)
      : new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 1.6, 14), barrelMat);
    const base = terrainHeight(p.x, p.z);
    m.position.set(p.x, base + (p.kind === "crate" ? 0.7 : 0.8), p.z);
    m.castShadow = true;
    m.receiveShadow = true;
    scene.add(m);
  }

  buildGrassField(scene);
  buildVegetation(scene);
  addDinosaurStatue(scene);
  return buildHillMarker(scene);
}

/**
 * One-off decorative statue: a downloaded FBX dinosaur model, planted on top
 * of the concrete barrier at (-17,-42) (paired with the rocks obstacle at
 * (0,-42) in the same view — a landmark, not gameplay-relevant). Loaded async
 * and added to the scene once ready; this is the one non-procedural mesh in
 * the arena (everything else here is canvas-textured primitives), so it's
 * wrapped in a try/catch — a failed/missing asset should never break the rest
 * of the arena.
 */
async function addDinosaurStatue(scene: THREE.Scene): Promise<void> {
  const barrier = OBSTACLES.find((o) => o.kind === "concrete" && o.x === -17 && o.z === -42);
  if (!barrier) return;
  try {
    const [fbx, tex] = await Promise.all([
      new FBXLoader().loadAsync("/assets/models/dinosaur/model.fbx"),
      new THREE.TextureLoader().loadAsync("/assets/models/dinosaur/textures/RGB.jpg"),
    ]);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85, metalness: 0.05 });
    fbx.traverse((n) => {
      if (n instanceof THREE.Mesh) {
        n.material = mat;
        n.castShadow = true;
        n.receiveShadow = true;
      }
    });

    // The source FBX's scale/pivot are whatever the artist exported — measure
    // it, scale to a fixed target height, then re-measure so the model's feet
    // (not its arbitrary origin) land exactly on the barrier's top surface.
    const rawHeight = new THREE.Box3().setFromObject(fbx).getSize(new THREE.Vector3()).y || 1;
    const targetHeight = 3.6;
    fbx.scale.setScalar(targetHeight / rawHeight);
    const feetY = new THREE.Box3().setFromObject(fbx).min.y;

    const topY = terrainHeight(barrier.x, barrier.z) + barrier.h;
    fbx.position.set(barrier.x, topY - feetY, barrier.z);
    fbx.rotation.y = Math.PI / 2; // broadside along the barrier's length
    scene.add(fbx);
  } catch (err) {
    console.warn("[arena] dinosaur statue failed to load", err);
  }
}
